import tinymce from 'tinymce/tinymce.min';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/paste';
import 'tinymce/themes/silver';
import 'tinymce/skins/ui/oxide/skin.css';
import 'tinymce/icons/default';
import {
    removeHighlights,
    addHighlights,
    registerTooltip,
} from '@/helpers/PushPullHighlights';

/**
 * TinyMCE Plugin that shows push and pull words in the editor text.
 *
 * @param {PushPullAnalysisService} pushPullAnalysisService
 * @param {AiWriterService} aiWriterService
 */
export default function (pushPullAnalysisService, aiWriterService, i18n) {
    /**
     *
     * @param {tinymce.Editor} editor
     * @constructor
     */
    tinymce.PluginManager.add('ppw-plugin', function (editor) {
        editor.addCommand('refreshAnalysis', async () => {
            const text = editor.getContent();
            let sanitizedText = removeHighlights(text);
            sanitizedText = sanitizedText.replace(/&nbsp;/g, '<br>');

            try {
                if (sanitizedText.length > 0) {
                    const { highlights } =
                        await pushPullAnalysisService.analyze(sanitizedText);
                    const highlightedText = addHighlights(
                        sanitizedText,
                        highlights,
                    );
                    editor.setContent(highlightedText);
                    editor.execCommand('registerTooltips');
                }
            } catch (error) {
                editor.setContent(sanitizedText);
                editor.execCommand('registerTooltips');
            }
        });

        editor.addCommand('registerTooltips', () => {
            // Tooltips are CSS based but don't know the bounds of their parent.
            // By default, they are above the parent and offset to the right.
            // If the space is not enough, we readjust them.
            const activators = editor.dom.$('[data-tooltip]');
            activators.each((i, activator) => {
                registerTooltip(activator);
            });
        });
        editor.ui.registry.addButton('optimize', {
            tooltip: i18n.t('components.textEditor.optimizeTooltip'),
            text: i18n.t('components.textEditor.optimizeBtn'),
            icon: 'ai-prompt',
            classes: 'optimize',
            onAction: () => {
                editor.execCommand('optimizeText');
            },
        });
        editor.ui.registry.addIcon(
            'ai-prompt',
            '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_1402_772)"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.0209 6.70598C14.6303 6.31545 13.9972 6.31545 13.6067 6.70598L3.70715 16.6055C3.31663 16.996 3.31663 17.6292 3.70715 18.0197L5.82847 20.141C6.219 20.5315 6.85216 20.5315 7.24269 20.141L17.1422 10.2415C17.5327 9.85099 17.5327 9.21782 17.1422 8.8273L15.0209 6.70598ZM16.4351 9.53426L14.3138 7.41293L11.4853 10.2414L13.6067 12.3627L16.4351 9.53426Z" fill="black"/><path d="M18.52 7.33333L17.8307 5.75972L16.32 5.04167L17.8307 4.32361L18.52 2.75L19.2093 4.32361L20.72 5.04167L19.2093 5.75972L18.52 7.33333Z" fill="black"/><path d="M18.52 16.5003L17.8307 14.9267L16.32 14.2087L17.8307 13.4906L18.52 11.917L19.2093 13.4906L20.72 14.2087L19.2093 14.9267L18.52 16.5003Z" fill="black"/><path d="M9.72 7.33333L9.03066 5.75972L7.52 5.04167L9.03066 4.32361L9.72 2.75L10.4093 4.32361L11.92 5.04167L10.4093 5.75972L9.72 7.33333Z" fill="black"/></g><defs><clipPath id="clip0_1402_772"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>',
        );

        editor.addCommand('optimizeText', async () => {
            editor.getBody().style.cursor = 'wait';
            editor.setMode('readonly');
            const text = editor.selection.getContent({ format: 'text' });
            try {
                let textnew = await aiWriterService.optimizeText(text);
                editor.selection.setContent(textnew.text);
                editor.execCommand('refreshAnalysis');
            } catch (error) {
                /* eslint-disable no-console */
                console.error('Error during text optimisation: ', error);
                /* eslint-enable no-console */
            } finally {
                editor.getBody().style.cursor = 'default';
                editor.setMode('design');
            }
        });

        editor.ui.registry.addButton('ppw-refresh', {
            tooltip: i18n.t('components.textEditor.analyzeTooltip'),
            text: i18n.t('components.textEditor.analyzeBtn'),
            icon: 'reload',
            onAction: () => {
                editor.execCommand('refreshAnalysis');
            },
        });
        editor.on('init', () => {
            editor.execCommand('refreshAnalysis');
        });
    });
    tinymce.PluginManager.add('customCopyPlugin', function (editor) {
        // Fügt einen neuen Befehl hinzu, der den Kopiervorgang ausführt
        editor.addCommand('CustomCopy', () => {
            if (!editor.selection.getContent({ format: 'text' })) {
                editor.execCommand('SelectAll');
            }
            editor.execCommand('Copy');
        });
        // Fügt einen Button in der Toolbar hinzu, der den neuen Befehl ausführt
        editor.ui.registry.addButton('copy-text', {
            tooltip: i18n.t('components.textEditor.copyTooltip'),
            text: i18n.t('components.textEditor.copyBtn'),
            icon: 'copy',
            onAction: () => {
                editor.execCommand('CustomCopy');
            },
        });
    });
}
